import { useMemo, useRef, useState } from "react";
import { useReadStats } from "../../hooks/use-read-stats";
import {
  type DrillAction,
  type DrillSession,
  type DrillSettings,
  createSession,
  reduceSession,
} from "../../lib/fingerspelling-drill-session";
import {
  flushPendingAnswers,
  postReadAnswer,
} from "../../lib/fingerspelling-stats-api";
import { type ReadStat, getWeakEntries } from "../../lib/fingerspelling-weak";
import { DrillResult } from "./DrillResult";
import { DrillStart } from "./DrillStart";
import { MakeDrill } from "./MakeDrill";
import { ReadDrill } from "./ReadDrill";

export const FingerspellingDrillPage = () => {
  // null のあいだは開始画面。1回のドリルの進行は保存せず、やめる・もどるで捨てる
  const [session, setSession] = useState<DrillSession | null>(null);
  const { stats, status, refetch } = useReadStats();
  // 開始画面に戻って記録を取り直したあとの案内。次に始めるときに消す
  const [notice, setNotice] = useState<string | null>(null);
  const retrying = useRef(false);
  const weakCount = useMemo(
    () =>
      status === "ready" && stats ? getWeakEntries(stats).length : undefined,
    [status, stats],
  );

  const startSession = (settings: DrillSettings, latest?: ReadStat[]) => {
    setNotice(null);
    if (!settings.weakOnly) return setSession(createSession(settings));
    const weak = getWeakEntries(latest ?? []);
    // 苦手が1つもないときは、問題が0個の画面になるので始めない
    if (weak.length > 0) setSession(createSession(settings, Math.random, weak));
  };

  /** 直前の回答の送信が終わってから、記録を取り直す（古い記録で苦手を決めない） */
  const refetchLatest = async () => {
    await flushPendingAnswers();
    return refetch();
  };

  /** 苦手ドリルのもういちどは、直前の回答を含めた最新の記録で苦手を決め直す */
  const retry = async (settings: DrillSettings) => {
    if (!settings.weakOnly) return startSession(settings);
    if (retrying.current) return;
    retrying.current = true;
    try {
      const result = await refetchLatest();
      if (result.isError || !result.data) return setSession(null);
      if (getWeakEntries(result.data).length === 0) {
        setNotice("にがては ぜんぶ なくなりました");
        return setSession(null);
      }
      startSession(settings, result.data);
    } finally {
      retrying.current = false;
    }
  };

  const goToStart = () => {
    setSession(null);
    void refetchLatest();
  };

  const dispatch = (action: DrillAction) => {
    // 「よむ」の回答は、結果が出た瞬間に記録する（待たない。失敗したら捨てる）
    if (
      action.type === "judge" &&
      session?.status === "playing" &&
      session.settings.mode === "read" &&
      session.phase === "asking"
    ) {
      const entry = session.questions[session.index];
      if (entry) void postReadAnswer(entry.id, action.result === "correct");
    }
    setSession((prev) => (prev ? reduceSession(prev, action) : prev));
  };

  if (!session) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">指文字ドリル</h1>
        {notice && (
          <p className="text-center text-sm text-neutral-600">{notice}</p>
        )}
        <DrillStart
          weakCount={weakCount}
          onStart={(settings) => startSession(settings, stats)}
        />
      </div>
    );
  }

  if (session.status === "finished") {
    return (
      <DrillResult
        session={session}
        onRetry={() => void retry(session.settings)}
        onBack={goToStart}
      />
    );
  }

  const Drill = session.settings.mode === "read" ? ReadDrill : MakeDrill;
  return <Drill session={session} dispatch={dispatch} onQuit={goToStart} />;
};
